import type {
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
} from "@mui/x-data-grid-premium";

const FILTERS_PARAM = "filters";
const SEARCH_PARAM = "search";
const SORT_PARAM = "sort";
const PAGE_PARAM = "page";

/** Prefixes a URL param so several grids on one page don't share keys. */
export const gridUrlParamKey = (param: string, namespace?: string): string =>
  namespace
    ? `${namespace}${param.charAt(0).toUpperCase()}${param.slice(1)}`
    : param;

export type GridUrlState = {
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  sortParam: string;
  page: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isLogicOperator = (value: unknown): value is GridLogicOperator =>
  value === "and" || value === "or";

const isGridFilterItem = (value: unknown): value is GridFilterItem => {
  if (!isRecord(value)) return false;

  return (
    typeof value.field === "string" &&
    typeof value.operator === "string" &&
    (value.id === undefined ||
      typeof value.id === "string" ||
      typeof value.id === "number") &&
    (value.value === undefined ||
      typeof value.value === "string" ||
      typeof value.value === "number")
  );
};

const isGridFilterModel = (value: unknown): value is GridFilterModel => {
  if (!isRecord(value) || !Array.isArray(value.items)) return false;

  return (
    value.items.every(isGridFilterItem) &&
    (value.logicOperator === undefined ||
      isLogicOperator(value.logicOperator)) &&
    (value.quickFilterLogicOperator === undefined ||
      isLogicOperator(value.quickFilterLogicOperator)) &&
    (value.quickFilterValues === undefined ||
      (Array.isArray(value.quickFilterValues) &&
        value.quickFilterValues.every(
          (quickFilterValue) => typeof quickFilterValue === "string"
        ))) &&
    (value.quickFilterExcludeHiddenColumns === undefined ||
      typeof value.quickFilterExcludeHiddenColumns === "boolean")
  );
};

const isGridSortItem = (value: unknown): value is GridSortModel[number] => {
  if (!isRecord(value) || typeof value.field !== "string") return false;

  return (
    value.sort === null ||
    value.sort === undefined ||
    value.sort === "asc" ||
    value.sort === "desc"
  );
};

const isGridSortModel = (value: unknown): value is GridSortModel =>
  Array.isArray(value) && value.every(isGridSortItem);

const parseJson = (value: string | null): unknown => {
  if (!value) return undefined;

  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

export const readGridUrlState = (
  searchParams: URLSearchParams,
  defaultSortModel: GridSortModel,
  namespace?: string
): GridUrlState => {
  const sortKey = gridUrlParamKey(SORT_PARAM, namespace);
  const pageKey = gridUrlParamKey(PAGE_PARAM, namespace);
  const search = (
    searchParams.get(gridUrlParamKey(SEARCH_PARAM, namespace)) ?? ""
  ).trim();
  const parsedFilterModel = parseJson(
    searchParams.get(gridUrlParamKey(FILTERS_PARAM, namespace))
  );
  const parsedSortModel = parseJson(searchParams.get(sortKey));
  const filterModel = isGridFilterModel(parsedFilterModel)
    ? parsedFilterModel
    : { items: [] };
  const urlPage = Number(searchParams.get(pageKey));

  return {
    filterModel: {
      ...filterModel,
      quickFilterValues: search ? [search] : [],
    },
    sortModel: isGridSortModel(parsedSortModel)
      ? parsedSortModel
      : defaultSortModel,
    sortParam: searchParams.get(sortKey) ?? "",
    page: Number.isInteger(urlPage) && urlPage > 1 ? urlPage - 1 : 0,
  };
};

export const updateGridUrlState = (
  searchParams: URLSearchParams,
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  namespace?: string,
  page = 0
): void => {
  const searchKey = gridUrlParamKey(SEARCH_PARAM, namespace);
  const filtersKey = gridUrlParamKey(FILTERS_PARAM, namespace);
  const sortKey = gridUrlParamKey(SORT_PARAM, namespace);
  const pageKey = gridUrlParamKey(PAGE_PARAM, namespace);
  const search = (filterModel.quickFilterValues ?? [])
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .trim();

  if (search) {
    searchParams.set(searchKey, search);
  } else {
    searchParams.delete(searchKey);
  }

  if (filterModel.items.length > 0) {
    searchParams.set(
      filtersKey,
      JSON.stringify(filterModel, (key, value: unknown) =>
        key === "quickFilterValues" || key === "fromInput" ? undefined : value
      )
    );
  } else {
    searchParams.delete(filtersKey);
  }

  if (sortModel.length > 0) {
    searchParams.set(sortKey, JSON.stringify(sortModel));
  } else {
    searchParams.delete(sortKey);
  }
  if (page > 0) {
    searchParams.set(pageKey, String(page + 1));
  } else {
    searchParams.delete(pageKey);
  }
};
